import { useMemo, useState } from 'react';
import debounce from 'lodash/debounce';

import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import InputText from '@/design_system/InputText/InputText';
import Stack from '@/design_system/Stack';
import {
  ArticleConditionForm,
  ArticleConditionFormType,
  useUpdateArticleCondition,
} from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useDatabaseTranslation } from '@/services/i18n';
import useViewPort from '@/utils/useViewport';

import { ConditionFormQuestion, useQuestionErrors } from './ConditionQuestion';

export const ConditionQuestionTextAndSelect = ({
  question,
  parentQuestion,
  type,
  editableArticleConditionForm,
}: {
  question: Extract<ConditionFormQuestion, { type: 'text-and-select' }>;
  parentQuestion?: ConditionFormQuestion;
  type: ArticleConditionFormType;
  editableArticleConditionForm?: ArticleConditionForm;
}) => {
  const { isMobile } = useViewPort();
  const { _db } = useDatabaseTranslation();
  const { article, view, initialConditionFormData, qualityControlFormData } = useArticleContext();

  const conditionForm =
    type === 'initial-condition'
      ? initialConditionFormData?.conditionForm
      : qualityControlFormData?.conditionForm;

  const errors = useQuestionErrors({ question, parentQuestion, type });
  const { mutateAsync: updateArticleCondition } = useUpdateArticleCondition({
    requestId: article.requestId,
    articleId: article.id,
    conditionForm,
  });

  const editable =
    !!editableArticleConditionForm &&
    (type === 'initial-condition' ? view.initialCondition.editable : view.qualityControl.editable);

  const config = question.config;

  const existingAnswerData =
    editableArticleConditionForm?.articleConditions.find(
      (articleCondition) => articleCondition.questionId === question.id
    )?.answerData ?? null;

  const [textValue, setTextValue] = useState<string | null>(existingAnswerData?.text ?? null);

  const selectValue =
    config.select.options.find((options) => options.value === existingAnswerData?.select) ?? null;

  const debouncedUpdateArticleCondition = useMemo(() => {
    return debounce(updateArticleCondition, 500);
  }, [updateArticleCondition]);

  const handleTextChange = (text: string) => {
    setTextValue(text);
    debouncedUpdateArticleCondition({
      questionId: question.id,
      answers: [
        {
          data: {
            select: selectValue?.value,
            text,
          },
        },
      ],
    })?.catch(() => {
      // Rollback to previous value if the update fails
      setTextValue(existingAnswerData?.text ?? null);
    });
  };

  const handleSelectChange = (
    select:
      | Extract<
          ConditionFormQuestion,
          { type: 'text-and-select' }
        >['config']['select']['options'][number]
      | null
  ) => {
    if (!select) {
      return;
    }
    updateArticleCondition({
      questionId: question.id,
      answers: [
        {
          data: {
            select: select?.value,
            text: textValue,
          },
        },
      ],
    });
  };

  if (!editable) {
    return null;
  }

  return (
    <Stack row={!isMobile} gap={isMobile ? '0.5rem' : '1rem'} ariaLabel={_db(question.title)}>
      <InputText
        style={{ flex: 1 }}
        placeholder={_db(config.input.placeholder)}
        aria-label={_db(config.input.label)}
        value={textValue ?? ''}
        onChange={handleTextChange}
        isInvalid={errors?.noAnswers || errors?.missingText}
      />
      <InputSelect
        style={{ flex: 1 }}
        placeholder={_db(config.select.placeholder)}
        options={config.select.options}
        value={selectValue}
        getOptionLabel={(option) => _db(option.label)}
        getOptionValue={(option) => option.value}
        onChange={handleSelectChange}
        isInvalid={errors?.noAnswers || errors?.missingSelect}
      />
    </Stack>
  );
};
